import { useEffect } from "react"
import { Drawer } from "vaul"
import { X } from "lucide-react"

import LargeButton from "@/shared/ui/LargeButton"
import { VaulBottomSheetShell } from "@/shared/ui/VaulBottomSheetShell"

import { useSegmentSheetStore } from "@/shared/store"

import { telegram } from "@/shared/lib"
import { AnalyticsEvent, trackAnalyticsEvent } from "@/shared/lib/analytics"

import {
  segmentSheetAnalyticsParams,
} from "./segmentSheetAnalytics"

/** `123 / 456` or `123/456` → large numerator + muted `/456` (Figma rank tile). */
function parseSegmentStatFraction(value: string): {
  primary: string
  slashRest: string
} | null {
  const m = value.trim().match(/^(\d+)\s*\/\s*(\d+)$/)
  if (!m) return null
  return { primary: m[1]!, slashRest: `/${m[2]!}` }
}

function SegmentStatValue({ value }: { value: string }) {
  const fraction = parseSegmentStatFraction(value)
  if (fraction) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-2xl font-bold leading-[29px] tracking-[-0.44px] text-white">
          {fraction.primary}
        </span>
        <div className="flex h-[29px] items-end pb-[3px]">
          <span className="text-sm font-semibold leading-[17px] tracking-[-0.5px] text-white/60">
            {fraction.slashRest}
          </span>
        </div>
      </div>
    )
  }
  return (
    <span className="text-2xl font-bold tracking-[-0.44px] text-white">
      {value}
    </span>
  )
}

const EMPTY_WIDGET = { title: "", content: "", special_text: "" } as const

const SegmentSheet = () => {
  const { isOpened, setIsOpened, data } = useSegmentSheetStore()

  useEffect(() => {
    if (!isOpened || data == null) return
    trackAnalyticsEvent(AnalyticsEvent.SegmentSheetView, {
      ...segmentSheetAnalyticsParams(data),
    })
  }, [isOpened, data])

  if (data == null) return null

  const widget = data.widget ?? EMPTY_WIDGET

  return (
    <Drawer.Root open={isOpened} onOpenChange={setIsOpened}>
      <Drawer.Portal>
        <VaulBottomSheetShell
          onFocusOutside={(evt) => evt.preventDefault()}
          onInteractOutside={(evt) => evt.preventDefault()}
          onPointerDownOutside={(evt) => evt.preventDefault()}
          panelClassName="flex h-[95%] flex-col rounded-t-3xl bg-primary-main"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto rounded-t-3xl">
            <Drawer.Handle className="mx-auto mt-2 h-1 w-8 shrink-0 rounded-full bg-tertiary-main" />

            <div className="flex shrink-0 items-center justify-between gap-2 px-4">
              <div className="w-8" />
              <div className="flex flex-col items-center gap-0.5">
                <Drawer.Title className="text-center text-lg font-semibold tracking-[-0.5px] text-white">
                  {data.title}
                </Drawer.Title>
              </div>
              <Drawer.Close asChild>
                <button className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-main text-white/60 transition-colors active:bg-tertiary-main">
                  <X className="h-4 w-4" />
                </button>
              </Drawer.Close>
            </div>

            <img
              src={
                data.segment.toLowerCase() === "predator"
                  ? "/images/segment-predator.png"
                  : data.segment.toLowerCase() === "sniper"
                    ? "/images/segment-sniper.png"
                    : "/images/segment-whale.png"
              }
              className="mx-auto size-[216px]"
            />

            <div className="grid grid-cols-2 gap-2 px-4">
              {data.columns.map((col, i) => (
                <div
                  key={col.key ?? `col-${i}`}
                  className="flex min-h-[88px] flex-col justify-between gap-[5px] rounded-2xl bg-white/10 p-2.5"
                >
                  <span className="text-sm leading-[17px] tracking-[-0.5px] text-white/60">
                    {col.name}
                  </span>
                  <SegmentStatValue value={col.value} />
                </div>
              ))}
            </div>

            {(widget.title || widget.content || widget.special_text) && (
              <div className="mx-4 flex flex-col rounded-[20px] bg-green-100 p-3 text-black-100">
                {widget.title && (
                  <span className="text-lg font-semibold">{widget.title}</span>
                )}
                {widget.content && (
                  <p className="mb-3 mt-2 text-sm">{widget.content}</p>
                )}
                {widget.special_text && (
                  <p className="rounded-2xl bg-black-100 p-3 text-sm font-semibold text-white">
                    {widget.special_text}
                  </p>
                )}
              </div>
            )}

            <div className="mt-auto px-4 pb-4">
              <LargeButton
                onClick={() => {
                  trackAnalyticsEvent(AnalyticsEvent.SegmentSheetCtaClick, {
                    ...segmentSheetAnalyticsParams(data),
                    cta_key: "primary",
                  })
                  telegram.openTelegramLink(data.button_url.trim())
                }}
              >
                {data.button_text}
              </LargeButton>
            </div>
          </div>
        </VaulBottomSheetShell>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

export default SegmentSheet
